<script setup lang="ts">
import { authService } from '@/services/AuthService'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps<{
  visible: boolean
  visibleMobile: boolean
}>()

const emit = defineEmits<{
  (e: 'toggle'): void
}>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const isMenuItemActive = (itemRoute: string) => {
  if (itemRoute === '/') {
    return route.path === '/'
  }
  return route.path === itemRoute || route.path.startsWith(`${itemRoute}/`)
}

const expandedMenus = ref<{ [key: string]: boolean }>({})
const isMobile = ref(false)
const supportModalVisible = ref(false)

// Detectar si está en un dispositivo móvil
const isMobileDevice = computed(() => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
})

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const toggleSubmenu = (menuKey: string) => {
  const isCurrentlyExpanded = expandedMenus.value[menuKey]
  
  // Close all other submenus
  Object.keys(expandedMenus.value).forEach(key => {
    if (key !== menuKey) {
      expandedMenus.value[key] = false
    }
  })
  
  // Toggle the clicked submenu
  expandedMenus.value[menuKey] = !isCurrentlyExpanded
}



const menuItems = [
  {
    key: 'home',
    label: t('menu.home'),
    icon: 'pi pi-chart-line',
    route: '/'
  },
  {
    key: 'companies',
    label: t('companies.title'),
    icon: 'pi pi-building',
    route: '/companies'
  },
  {
    key: 'allEmployees',
    label: t('menu.allEmployees'),
    icon: 'pi pi-users',
    route: '/all-employees'
  },
  {
    key: 'subscriptions',
    label: t('menu.subscriptions'),
    icon: 'pi pi-credit-card',
    route: '/subscriptions'
  }
]

const signOut = async () => {
  localStorage.clear();
  await authService.logout()
  router.replace('login')
}

const openSupportModal = () => {
  supportModalVisible.value = true
}

const closeSupportModal = () => {
  supportModalVisible.value = false
}

const openWhatsApp = () => {
  window.open('https://wa.me/644154001', '_blank')
  closeSupportModal()
}

const openEmail = () => {
  window.open('mailto:info@kargotr.com', '_blank')
  closeSupportModal()
}
</script>

<template>
  <div 
    class="layout-sidebar" 
    :class="{ 
      'layout-sidebar-collapsed': !visible && !isMobile,
      'layout-sidebar-mobile': isMobile,
      'layout-sidebar-mobile-visible': visibleMobile
    }"
  >
    <div class="sidebar-header" v-if="!isMobile">
      <div class="flex align-items-center justify-content-between">
        <Image
          v-if="visible || isMobile"
          src="/logo.png"
          alt="logo"
          height="40"
          style="cursor: pointer"
          @click="router.push('/')"
        />
        <Button
          :icon="visible ? 'pi pi-chevron-left' : 'pi pi-chevron-right'"
          text
          rounded
          @click="emit('toggle')"
          class="toggle-button"
          :class="{ 'toggle-button-collapsed': !visible }"
        />
      </div>
    </div>

    <div class="layout-menu">
      <div v-for="item in menuItems" :key="item.key" class="menu-item">
        <RouterLink
          :to="item.route"
          custom
          v-slot="{ navigate, href }"
        >
          <a
            :href="href"
            class="menu-header"
            :class="{ 'menu-header-active': isMenuItemActive(item.route) }"
            @click="navigate"
          >
            <i :class="item.icon"></i>
            <span v-if="visible || isMobile">{{ item.label }}</span>
          </a>
        </RouterLink>
      </div>
    </div>

    <div class="sidebar-footer">
      <Button
        class="supportButton w-full mb-2"
        :label="visible || isMobile ? $t('menu.support') : ''"
        icon="pi pi-comments"
        iconPos="left"
        link
        @click="openSupportModal"
      />
      <Button
        class="signOutButton w-full"
        :label="visible || isMobile ? $t('login.signOut') : ''"
        icon="pi pi-sign-out"
        iconPos="right"
        link
        @click="signOut"
      />
    </div>
  </div>

  <!-- Modal de soporte -->
  <Dialog
    v-model:visible="supportModalVisible"
    :modal="true"
    :header="$t('support.modal.title')"
    :style="{ width: '450px' }"
    :closable="true"
    @hide="closeSupportModal"
  >
    <div class="support-modal-content">
      <p class="support-description">{{ $t('support.modal.description') }}</p>
      
      <!-- WhatsApp Section -->
      <div class="contact-section">
        <h4 class="contact-section-title">
          <i class="pi pi-whatsapp"></i>
          {{ $t('support.modal.whatsappSection') }}
        </h4>
        <div class="contact-option" @click="openWhatsApp">
          <div class="contact-info">
            <i class="pi pi-phone"></i>
            <span>{{ $t('support.modal.phoneNumber') }}</span>
          </div>
          <Button
            :label="$t('support.modal.openChat')"
            icon="pi pi-external-link"
            size="small"
            text
            class="contact-button"
          />
        </div>
      </div>

      <!-- Email Section -->
      <div class="contact-section">
        <h4 class="contact-section-title">
          <i class="pi pi-envelope"></i>
          {{ $t('support.modal.emailSection') }}
        </h4>
        <div class="contact-option" @click="openEmail">
          <div class="contact-info">
            <i class="pi pi-at"></i>
            <span>{{ $t('support.modal.email') }}</span>
          </div>
          <Button
            :label="$t('support.modal.sendEmail')"
            icon="pi pi-external-link"
            size="small"
            text
            class="contact-button"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.layout-sidebar {
  position: fixed;
  width: 280px;
  height: 100vh;
  z-index: 999;
  overflow-y: auto;
  background-color: var(--surface-card);
  border-right: 1px solid var(--surface-border);
  display: flex;
  flex-direction: column;
  transition: all 0.3s;
  left: 0;
  top: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.layout-sidebar-collapsed {
  width: 4rem;
}

.layout-sidebar-mobile {
  transform: translateX(-100%);
  width: 280px;
  visibility: hidden;
}

.layout-sidebar-mobile-visible {
  transform: translateX(0);
  visibility: visible;
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--surface-border);
  background-color: var(--surface-card);
}

.layout-menu {
  flex: 1;
  padding: 1rem 0;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid var(--surface-border);
  background-color: var(--surface-card);
}

.menu-item {
  margin-bottom: 0.25rem;
}

.menu-header {
  padding: 0.75rem 1.25rem;
  color: var(--text-color-secondary);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  font-size: 0.95rem;
  text-decoration: none;
}

.menu-header:hover {
  background-color: var(--surface-hover);
  color: var(--text-color);
}

.menu-header-active {
  background-color: var(--primary-100);
  color: var(--primary-color);
  border-left: 3px solid var(--primary-color);
}

.menu-header-active i {
  color: var(--primary-color);
}

.menu-header i:first-child {
  margin-right: 0.75rem;
  font-size: 1.1rem;
  transition: color 0.2s;
}

.menu-header:hover i:first-child {
  color: var(--text-color);
}

.submenu-icon {
  margin-left: auto;
  transition: transform 0.2s;
  font-size: 0.8rem;
}

.submenu-icon-expanded {
  transform: rotate(180deg);
}

.layout-sidebar-collapsed .submenu-icon {
  display: block;
  margin-left: 0;
  margin-top: 0.25rem;
}

.submenu {
  background-color: var(--surface-ground);
  transition: all 0.2s;
}

.submenu-item {
  padding: 0.75rem 1.25rem 0.75rem 2.5rem;
  color: var(--text-color-secondary);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
}

.submenu-item:hover {
  background-color: var(--surface-hover);
  color: var(--text-color);
}

.submenu-item i {
  margin-right: 0.75rem;
  font-size: 1.1rem;
  transition: color 0.2s;
}

.submenu-item:hover i {
  color: var(--text-color);
}

.submenu-collapsed .submenu-item {
  padding: 0.75rem;
  justify-content: center;
}

.submenu-collapsed .submenu-item i {
  margin-right: 0;
}

.layout-sidebar-collapsed .menu-header {
  padding: 0.75rem;
  justify-content: center;
}

.layout-sidebar-collapsed .menu-header i:first-child {
  margin-right: 0;
}

.layout-sidebar-collapsed .submenu-icon {
  display: none;
}

.supportButton {
  color: var(--text-color-secondary);
  width: 100%;
  justify-content: flex-start;
  padding: 0.75rem 1.25rem;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.layout-sidebar-collapsed .supportButton {
  justify-content: center;
  padding: 0.75rem;
}

.supportButton:hover {
  background-color: var(--surface-hover);
  color: var(--text-color);
}

.supportButton .p-button-icon {
  font-size: 1.1rem;
  transition: color 0.2s;
}

.supportButton:hover .p-button-icon {
  color: var(--text-color);
}

.signOutButton {
  color: var(--text-color-secondary);
  width: 100%;
  justify-content: flex-start;
  padding: 0.75rem 1.25rem;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.layout-sidebar-collapsed .signOutButton {
  justify-content: center;
  padding: 0.75rem;
}

.signOutButton:hover {
  background-color: var(--surface-hover);
  color: var(--text-color);
}

.signOutButton .p-button-icon {
  font-size: 1.1rem;
  transition: color 0.2s;
}

.signOutButton:hover .p-button-icon {
  color: var(--text-color);
}

/* Estilos para el modal de soporte */
.support-modal-content {
  text-align: left;
  padding: 1rem 0;
}

.support-description {
  margin-bottom: 1.5rem;
  color: var(--text-color);
  font-size: 1rem;
  line-height: 1.5;
  text-align: center;
}

.phone-number-section {
  margin-bottom: 2rem;
  padding: 1rem;
  background-color: var(--surface-ground);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  cursor: pointer;
}

.phone-number {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
}

.phone-number i {
  color: var(--primary-color);
}

.action-buttons {
  margin-top: 1.5rem;
}

.whatsapp-button {
  background-color: #25D366;
  border-color: #25D366;
  color: white;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  transition: all 0.3s;
}

.whatsapp-button:hover {
  background-color: #20BA5A;
  border-color: #20BA5A;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.manual-instruction {
  color: var(--text-color-secondary);
  font-style: italic;
  margin: 0;
}

/* Nuevos estilos para las secciones de contacto */
.contact-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: var(--surface-ground);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.contact-section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.contact-section-title i {
  font-size: 1.1rem;
}

.contact-section-title .pi-whatsapp {
  color: #25D366;
}

.contact-section-title .pi-envelope {
  color: var(--primary-color);
}

.contact-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background-color: var(--surface-card);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.contact-option:hover {
  background-color: var(--surface-hover);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.contact-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  color: var(--text-color);
}

.contact-info i {
  color: var(--text-color-secondary);
  font-size: 1rem;
}

.contact-button {
  color: var(--primary-color);
  font-size: 0.875rem;
}

.contact-button:hover {
  color: var(--primary-color-text);
  background-color: var(--primary-color);
}

@media screen and (max-width: 768px) {
  .layout-sidebar {
    top: 4rem;
    height: calc(100vh - 4rem);
    visibility: visible;
  }
}
</style>
